import { useEffect, useState } from 'react'
import {
  ArrowRight, Building2, CalendarDays, ClipboardList, MapPin, Network,
  Plus, Star, Users,
} from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, apiGet, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { useApi } from '../access/lib.js'
import { useSaveBar } from '../shell/editing.js'
import { Link, navigate } from '../router.js'
import './rh.css'
import './planning.css'
import { POSITION_PALETTE, TONE_LABELS, shiftTone } from './planning-model.js'
import { useMenuPersonne } from './menu-personne.js'

/**
 * SIRH LOT 2.5 — PRODUCT RESET (docs/hr/product-reset-v1.md).
 *
 * DOCTRINE : UNE TUILE DE NAVIGATION SERT À NAVIGUER. UNE PAGE DE GESTION
 * SERT À GÉRER. Jamais les deux. Une tuile n'a NI table, NI CRUD, NI
 * scrollbar interne — un compte réel, une phrase, une flèche.
 */

/* ═══════════ Tuile de navigation (partagée) ═══════════ */

function HubTile(props: {
  route?: string
  icon: typeof Users
  title: string
  headline?: string | null
  description: string
  meta?: string | null
  size?: 'lg' | 'md' | 'sm' | 'wide'
  planned?: boolean
}) {
  const inner = (
    <>
      <span className="hub-tile__icon"><props.icon size={20} aria-hidden="true" /></span>
      <span className="hub-tile__body">
        <span className="hub-tile__title">{props.title}</span>
        {props.headline ? <span className="hub-tile__headline">{props.headline}</span> : null}
        <span className="hub-tile__desc">{props.description}</span>
        {props.meta ? <span className="hub-tile__meta">{props.meta}</span> : null}
      </span>
      {props.planned
        ? <span className="lc-pill lc-pill--idle hub-tile__pill">À venir</span>
        : <ArrowRight size={16} className="hub-tile__arrow" aria-hidden="true" />}
    </>
  )
  const cls = `hub-tile hub-tile--${props.size ?? 'md'}${props.planned ? ' hub-tile--planned' : ''}`
  if (!props.route) return <div className={cls} aria-disabled="true">{inner}</div>
  return <Link href={props.route} className={cls} aria-label={props.title}>{inner}</Link>
}

/* ═══════════ ORGANISATION — HOME (§20-30) ═══════════ */

interface OrgSummary { teams: number; positions: number; functions: number; skills: number; managers: number; relations: number; locations: string[] }

export function HrOrgHubPage() {
  const d = useApi<OrgSummary>('/api/v1/hr/org-summary')
  const s = d.data && 'teams' in d.data ? d.data : null
  const n = (v: number | undefined, sg: string, pl: string) => v === undefined ? '…' : `${v} ${v > 1 ? pl : sg}`
  return (
    <main className="lc-page lc-page--management lc-stagger hub-page">
      <Trail />
      <SmartBack fallback="/equipe" />
      <header className="hub-hero">
        <h1 className="lc-title">Organisation</h1>
        <p className="hub-hero__sub">Structurez les équipes, métiers et référentiels utilisés par les RH, le planning et les permissions.</p>
      </header>
      <div className="hub-grid hub-grid--org">
        <HubTile route="/equipe/organisation/equipes" icon={Users} size="lg"
          title="Équipes" headline={s ? n(s.teams, 'équipe', 'équipes') : '…'}
          description="Responsables, membres et organisation du travail." />
        <HubTile route="/equipe/organisation/postes" icon={Building2} size="lg"
          title="Postes" headline={s ? n(s.positions, 'poste', 'postes') : '…'}
          description="Métiers principaux et fiches de poste." />
        <HubTile route="/equipe/organisation/fonctions" icon={ClipboardList}
          title="Fonctions" headline={s ? n(s.functions, 'fonction', 'fonctions') : '…'}
          description="Capacités opérationnelles cumulables." />
        <HubTile route="/equipe/organisation/competences" icon={Star}
          title="Compétences" headline={s ? n(s.skills, 'compétence', 'compétences') : '…'}
          description="Catalogue et niveaux de maîtrise." />
        <HubTile route="/equipe/organisation/lieux" icon={MapPin}
          title="Lieux" headline={s ? n(s.locations.length, 'lieu', 'lieux') : '…'}
          description="Les lieux de travail du club."
          meta={s && s.locations.length > 0 ? s.locations.slice(0, 3).join(' · ') : null} />
        <HubTile route="/equipe/organisation/organigramme" icon={Network} size="wide"
          title="Organigramme" headline={s ? `${s.managers} responsable${s.managers > 1 ? 's' : ''}` : '…'}
          description="Qui répond à qui — responsables principaux, secondaires et opérationnels, sans pyramide."
          meta={s && s.relations > 0 ? `${s.relations} rattachement${s.relations > 1 ? 's' : ''}` : null} />
        <HubTile icon={ClipboardList} planned
          title="Missions" description="Fiches de mission et procédures connectées aux postes et au planning." />
      </div>
    </main>
  )
}

/* ═══════════ POSTES — liste (§31-32) ═══════════ */

interface RefData {
  positions: { id: string; name: string; family: string | null; description: string | null; responsibilities: string | null; active: boolean; assigned: number }[]
  functions: { id: string; name: string; description: string | null; active: boolean; assigned: number }[]
  locations: { id: string; code: string; name: string; kind: string; legacy_label: string | null; active: boolean; assigned: number }[]
}

function useCanOrgEdit(): boolean {
  const perms = usePermissions()
  return !perms.loading && perms.permissions.includes('hr.org.edit')
}

function CreateModal(props: { kind: 'POSITION' | 'FUNCTION' | 'LOCATION'; open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const label = props.kind === 'POSITION' ? 'poste' : props.kind === 'FUNCTION' ? 'fonction' : 'lieu'
  const save = async () => {
    const body: Record<string, unknown> = { kind: props.kind, name: name.trim() }
    if (props.kind === 'LOCATION' && code.trim()) body['code'] = code.trim()
    const r = await adminMutate<{ error?: string; message?: string; id?: string }>('POST', '/api/v1/hr/referentiels', body)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `${name.trim()} — ${label} créé${props.kind === 'FUNCTION' ? 'e' : ''}.`)
    setName(''); setCode('')
    props.onCreated(r.data.id ?? '')
  }
  return (
    <Modal open={props.open} onClose={props.onClose} ariaLabel={`Nouveau ${label}`} width={440}>
      <div className="org-editor">
        <h3>Nouveau {label}</h3>
        <Field label="Nom"><TextInput value={name} onChange={setName} autoFocus placeholder={props.kind === 'POSITION' ? 'Barman, Régisseur lumière…' : props.kind === 'FUNCTION' ? 'Service, Console lumière…' : 'ROOM 4, Vestiaire…'} /></Field>
        {props.kind === 'LOCATION' ? <Field label="Code (court, unique)"><TextInput value={code} onChange={setCode} placeholder="R4, VEST…" /></Field> : null}
        <div className="org-editor__foot">
          <button className="lc-btn lc-btn--ghost" onClick={props.onClose}>Annuler</button>
          <button className="lc-btn lc-btn--primary" disabled={!name.trim()} onClick={() => void save()}>Créer</button>
        </div>
      </div>
    </Modal>
  )
}

export function HrPositionsPage() {
  const d = useApi<RefData>('/api/v1/hr/referentiels')
  const canEdit = useCanOrgEdit()
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const rows = (d.data && 'positions' in d.data ? d.data.positions : [])
    .filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()) || (p.family ?? '').toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead org-pagehead--row">
        <div>
          <h1 className="lc-title" style={{ margin: 0 }}>Postes</h1>
          <p className="hub-hero__sub">Définissez les métiers principaux utilisés dans les dossiers salariés et le planning.</p>
        </div>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => setCreating(true)}><Plus size={15} /> Nouveau poste</button> : null}
      </header>
      <input className="lc-input mg-search" placeholder="Rechercher un poste…" value={q} onChange={(e) => setQ(e.target.value)} />
      {rows.length === 0 ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body"><p className="lc-hint" style={{ margin: 0 }}>{q ? 'Aucun poste ne correspond.' : 'Aucun poste défini. Le poste est la place RH principale — Barman, Régisseur lumière, Directeur d’exploitation…'}</p></div></section>
      ) : (
        <section className="lc-panel gd-panel mg-list">
          {rows.map((p) => (
            <button key={p.id} className="mg-row" onClick={() => navigate(`/equipe/organisation/postes/${p.id}`)}>
              <span className="mg-row__main">
                <strong>{p.name}</strong>
                {p.description ? <span className="mg-row__desc">{p.description}</span> : null}
              </span>
              <span className="mg-row__facts">
                {p.family ? <span className="lc-pill lc-pill--idle">{p.family}</span> : null}
                <span className="mg-row__count">{p.assigned} affecté{p.assigned > 1 ? 's' : ''}</span>
                {!p.active ? <span className="lc-pill lc-pill--warning">Désactivé</span> : null}
                <ArrowRight size={15} aria-hidden="true" />
              </span>
            </button>
          ))}
        </section>
      )}
      <CreateModal kind="POSITION" open={creating} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); if (id) navigate(`/equipe/organisation/postes/${id}`) }} />
    </main>
  )
}

/* ═══════════ POSTE — détail avec Save Bar (§33-34) ═══════════ */

interface PositionDetail {
  position: { id: string; name: string; family: string | null; description: string | null; responsibilities: string | null; color: string | null; active: boolean; updated_at: string }
  people: { person_id: string; display_name: string; effective_from: string }[]
  history: { display_name: string; effective_from: string; effective_until: string }[]
}

export function HrPositionDetailPage({ id }: { id: string }) {
  const [d, setD] = useState<PositionDetail | null>(null)
  const [form, setForm] = useState({ name: '', family: '', description: '', responsibilities: '', color: '', active: true })
  const [snapshot, setSnapshot] = useState(form)
  const canEdit = useCanOrgEdit()
  const menu = useMenuPersonne()

  const load = () => {
    apiGet<PositionDetail>(`/api/v1/hr/positions/${id}`).then((r) => {
      if ('position' in r) {
        setD(r)
        const f = { name: r.position.name, family: r.position.family ?? '', description: r.position.description ?? '', responsibilities: r.position.responsibilities ?? '', color: r.position.color ?? '', active: r.position.active }
        setForm(f); setSnapshot(f)
      }
    }).catch(() => undefined)
  }
  useEffect(load, [id])

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot)
  useSaveBar({
    dirty: dirty && canEdit,
    count: dirty ? 1 : 0,
    saveLabel: 'Enregistrer le poste',
    invalid: form.name.trim() === '' ? 'Le nom est requis.' : null,
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/referentiels', {
        kind: 'POSITION', id, name: form.name.trim(),
        family: form.family.trim() || undefined, description: form.description.trim() || undefined,
        responsibilities: form.responsibilities.trim() || undefined, color: form.color || null, active: form.active,
      })
      if (r.status >= 400) return r.data.message ?? 'Refusé.'
      load()
      return true as const
    },
    onCancel: () => setForm(snapshot),
    successMessage: 'Poste enregistré.',
  })

  if (!d) return <main className="lc-page"><p className="lc-hint">Chargement…</p></main>
  return (
    <main className="lc-page lc-page--readable lc-stagger">
      <Trail leaf={snapshot.name} />
      <SmartBack fallback="/equipe/organisation/postes" />
      <header className="org-pagehead">
        <h1 className="lc-title" style={{ margin: 0 }}>{snapshot.name}</h1>
        <p className="hub-hero__sub">{d.people.length} personne{d.people.length > 1 ? 's' : ''} affectée{d.people.length > 1 ? 's' : ''} à ce poste aujourd’hui.</p>
      </header>

      <div className="mg-detail">
        <section className="lc-panel gd-panel"><div className="lc-panel__body mg-form">
          <h2 className="lc-section-title">Fiche de poste</h2>
          <Field label="Nom"><TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} disabled={!canEdit} /></Field>
          <Field label="Famille" helper="Bar, Technique, Accueil… (libre)"><TextInput value={form.family} onChange={(v) => setForm({ ...form, family: v })} disabled={!canEdit} /></Field>
          <Field label="Couleur planning" helper="Le poste est la couleur métier principale du planning — accent subtil, jamais un aplat criard.">
            <div className="org-colorpick">
              {POSITION_PALETTE.map((c) => (
                <button key={c} type="button" disabled={!canEdit} aria-label={TONE_LABELS[c]} title={TONE_LABELS[c]} aria-pressed={form.color === c}
                  className={`org-swatch pl-tone--${c}${form.color === c ? ' org-swatch--on' : ''}`} onClick={() => setForm({ ...form, color: c })} />
              ))}
              <button type="button" disabled={!canEdit} className={`org-swatch org-swatch--none${form.color === '' ? ' org-swatch--on' : ''}`} aria-label="Automatique" aria-pressed={form.color === ''} onClick={() => setForm({ ...form, color: '' })}>auto</button>
            </div>
            <div className={`org-shiftpreview pl-chip pl-chip--${form.color ? form.color : shiftTone({ position_id: id, position_color: null })}`} aria-hidden="true">
              <span className="pl-chip__head"><span className="pl-chip__pos">{snapshot.name || 'Poste'}</span></span>
              <span className="pl-chip__time">23:00–06:00</span>
              <span className="pl-chip__meta"><span>ROOM 1</span><span className="pl-chip__dur">7h</span></span>
            </div>
          </Field>
          <Field label="Description"><textarea className="lc-input mg-textarea" value={form.description} disabled={!canEdit} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></Field>
          <Field label="Responsabilités"><textarea className="lc-input mg-textarea" value={form.responsibilities} disabled={!canEdit} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} rows={4} /></Field>
          <label className="org-leadcheck"><input type="checkbox" checked={form.active} disabled={!canEdit} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Poste actif (proposé dans les affectations)</label>
          <p className="lc-hint">Compétences requises, formations et régime par défaut arriveront avec leurs lots — rien n’est affiché tant que rien n’existe.</p>
        </div></section>

        <div className="mg-side">
          <section className="lc-panel gd-panel"><div className="lc-panel__body">
            <h2 className="lc-section-title">Personnes</h2>
            {d.people.length === 0 ? <p className="lc-hint">Personne n’occupe ce poste. L’affectation se fait depuis la fiche salarié → Emploi.</p> :
              d.people.map((p) => (
                <div className="rh-assign" key={p.person_id}>
                  <Link className="org-memberlink" href={`/equipe/rh/salaries/${p.person_id}`} {...menu.personne({ id: p.person_id, nom: p.display_name })}>{p.display_name}</Link>
                  <span className="rh-assign__meta">depuis {p.effective_from}</span>
                </div>
              ))}
          </div></section>
          {d.history.length > 0 ? (
            <section className="lc-panel gd-panel"><div className="lc-panel__body">
              <h2 className="lc-section-title">Historique</h2>
              {d.history.map((h, i) => (
                <div className="rh-assign" key={i}>
                  <span>{h.display_name}</span>
                  <span className="rh-assign__meta">{h.effective_from} → {h.effective_until}</span>
                </div>
              ))}
            </div></section>
          ) : null}
        </div>
      </div>
    </main>
  )
}

/* ═══════════ FONCTIONS (§35) ═══════════ */

export function HrFunctionsPage() {
  const d = useApi<RefData>('/api/v1/hr/referentiels')
  const canEdit = useCanOrgEdit()
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const rows = (d.data && 'functions' in d.data ? d.data.functions : [])
    .filter((f) => !q.trim() || f.name.toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead org-pagehead--row">
        <div>
          <h1 className="lc-title" style={{ margin: 0 }}>Fonctions</h1>
          <p className="hub-hero__sub">Les capacités opérationnelles réellement exercées — une personne peut en cumuler plusieurs.</p>
        </div>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => setCreating(true)}><Plus size={15} /> Nouvelle fonction</button> : null}
      </header>
      <input className="lc-input mg-search" placeholder="Rechercher une fonction…" value={q} onChange={(e) => setQ(e.target.value)} />
      {rows.length === 0 ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body"><p className="lc-hint" style={{ margin: 0 }}>{q ? 'Aucune fonction ne correspond.' : 'Aucune fonction définie.'}</p></div></section>
      ) : (
        <section className="lc-panel gd-panel mg-list">
          {rows.map((f) => (
            <button key={f.id} className="mg-row" onClick={() => navigate(`/equipe/organisation/fonctions/${f.id}`)}>
              <span className="mg-row__main"><strong>{f.name}</strong>{f.description ? <span className="mg-row__desc">{f.description}</span> : null}</span>
              <span className="mg-row__facts">
                <span className="mg-row__count">{f.assigned} personne{f.assigned > 1 ? 's' : ''}</span>
                <ArrowRight size={15} aria-hidden="true" />
              </span>
            </button>
          ))}
        </section>
      )}
      <CreateModal kind="FUNCTION" open={creating} onClose={() => setCreating(false)} onCreated={(fid) => { setCreating(false); if (fid) navigate(`/equipe/organisation/fonctions/${fid}`) }} />
    </main>
  )
}

interface FunctionDetail {
  fn: { id: string; name: string; description: string | null; active: boolean }
  people: { person_id: string; display_name: string; effective_from: string }[]
}

export function HrFunctionDetailPage({ id }: { id: string }) {
  const [d, setD] = useState<FunctionDetail | null>(null)
  const [form, setForm] = useState({ name: '', description: '', active: true })
  const [snapshot, setSnapshot] = useState(form)
  const canEdit = useCanOrgEdit()
  const menu = useMenuPersonne()
  const load = () => {
    apiGet<FunctionDetail>(`/api/v1/hr/functions/${id}`).then((r) => {
      if ('fn' in r) {
        setD(r)
        const f = { name: r.fn.name, description: r.fn.description ?? '', active: r.fn.active }
        setForm(f); setSnapshot(f)
      }
    }).catch(() => undefined)
  }
  useEffect(load, [id])
  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot)
  useSaveBar({
    dirty: dirty && canEdit, count: dirty ? 1 : 0, saveLabel: 'Enregistrer la fonction',
    invalid: form.name.trim() === '' ? 'Le nom est requis.' : null,
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/referentiels', {
        kind: 'FUNCTION', id, name: form.name.trim(), description: form.description.trim() || undefined, active: form.active,
      })
      if (r.status >= 400) return r.data.message ?? 'Refusé.'
      load(); return true as const
    },
    onCancel: () => setForm(snapshot),
    successMessage: 'Fonction enregistrée.',
  })
  if (!d) return <main className="lc-page"><p className="lc-hint">Chargement…</p></main>
  return (
    <main className="lc-page lc-page--readable lc-stagger">
      <Trail leaf={snapshot.name} />
      <SmartBack fallback="/equipe/organisation/fonctions" />
      <header className="org-pagehead">
        <h1 className="lc-title" style={{ margin: 0 }}>{snapshot.name}</h1>
        <p className="hub-hero__sub">{d.people.length} personne{d.people.length > 1 ? 's' : ''} exerce{d.people.length > 1 ? 'nt' : ''} cette fonction.</p>
      </header>
      <div className="mg-detail">
        <section className="lc-panel gd-panel"><div className="lc-panel__body mg-form">
          <h2 className="lc-section-title">Fiche</h2>
          <Field label="Nom"><TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} disabled={!canEdit} /></Field>
          <Field label="Description"><textarea className="lc-input mg-textarea" value={form.description} disabled={!canEdit} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></Field>
          <label className="org-leadcheck"><input type="checkbox" checked={form.active} disabled={!canEdit} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Fonction active</label>
        </div></section>
        <div className="mg-side">
          <section className="lc-panel gd-panel"><div className="lc-panel__body">
            <h2 className="lc-section-title">Personnes capables</h2>
            {d.people.length === 0 ? <p className="lc-hint">Personne pour l’instant — l’attribution se fait depuis la fiche salarié → Emploi.</p> :
              d.people.map((p) => (
                <div className="rh-assign" key={p.person_id}>
                  <Link className="org-memberlink" href={`/equipe/rh/salaries/${p.person_id}`} {...menu.personne({ id: p.person_id, nom: p.display_name })}>{p.display_name}</Link>
                  <span className="rh-assign__meta">depuis {p.effective_from}</span>
                </div>
              ))}
          </div></section>
        </div>
      </div>
    </main>
  )
}

/* ═══════════ LIEUX (§36) ═══════════ */

export function HrLocationsPage() {
  const d = useApi<RefData>('/api/v1/hr/referentiels')
  const canEdit = useCanOrgEdit()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)
  const rows = d.data && 'locations' in d.data ? d.data.locations : []
  const rename = async () => {
    if (!editing) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/referentiels', { kind: 'LOCATION', id: editing.id, name: editing.name.trim() })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Lieu enregistré.')
    setEditing(null)
    d.reload()
  }
  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead org-pagehead--row">
        <div>
          <h1 className="lc-title" style={{ margin: 0 }}>Lieux</h1>
          <p className="hub-hero__sub">Les lieux de travail utilisés par le planning et les affectations.</p>
        </div>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => setCreating(true)}><Plus size={15} /> Nouveau lieu</button> : null}
      </header>
      <section className="lc-panel gd-panel mg-list">
        {rows.map((l) => (
          <div key={l.id} className="mg-row mg-row--static">
            <span className="mg-row__main">
              <strong>{l.name}</strong>
              <span className="mg-row__desc">{l.kind === 'ROOM' ? 'Salle' : l.kind === 'SERVICE' ? 'Service' : 'Autre'} · code {l.code}</span>
            </span>
            <span className="mg-row__facts">
              <span className="mg-row__count">{l.assigned} affecté{l.assigned > 1 ? 's' : ''}</span>
              {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => setEditing({ id: l.id, name: l.name })}>Renommer</button> : null}
            </span>
          </div>
        ))}
      </section>
      {rows.some((l) => l.legacy_label) ? (
        <p className="lc-hint mg-legacy">
          Historique / migration : {rows.filter((l) => l.legacy_label).map((l) => `${l.legacy_label} → ${l.name}`).join(' · ')}.
          Les anciens noms n’apparaissent nulle part ailleurs.
        </p>
      ) : null}
      <CreateModal kind="LOCATION" open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); d.reload() }} />
      <Modal open={editing !== null} onClose={() => setEditing(null)} ariaLabel="Renommer le lieu" width={420}>
        {editing ? (
          <div className="org-editor">
            <h3>Renommer le lieu</h3>
            <Field label="Nom"><TextInput value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} autoFocus /></Field>
            <div className="org-editor__foot">
              <button className="lc-btn lc-btn--ghost" onClick={() => setEditing(null)}>Annuler</button>
              <button className="lc-btn lc-btn--primary" disabled={!editing.name.trim()} onClick={() => void rename()}>Enregistrer</button>
            </div>
          </div>
        ) : null}
      </Modal>
    </main>
  )
}

/* ═══════════ TEMPS — HOME (§40-41) ═══════════ */

interface WeekLite { week_start: string; shifts: { id: string; person_id: string }[]; publication: { version: number; pending_changes: number } | null }

interface TempsHub {
  stations_total: number; stations_online: number
  to_approve: number; anomalies: number; leave_requests: number; campaigns_open: number
}

export function HrTempsHomePage() {
  const [d, setD] = useState<WeekLite | null>(null)
  const [hub, setHub] = useState<TempsHub | null>(null)
  useEffect(() => {
    apiGet<WeekLite>('/api/v1/hr/planning/week').then((r) => { if ('shifts' in r) setD(r) }).catch(() => undefined)
    apiGet<TempsHub>('/api/v1/hr/time/hub').then(setHub).catch(() => undefined)
  }, [])
  const persons = d ? new Set(d.shifts.map((s) => s.person_id)).size : 0
  const headline = d
    ? d.shifts.length === 0
      ? 'Semaine vide'
      : `${d.shifts.length} shift${d.shifts.length > 1 ? 's' : ''} · ${persons} salarié${persons > 1 ? 's' : ''}`
    : '…'
  const meta = d
    ? d.publication
      ? `v${d.publication.version} publiée${d.publication.pending_changes > 0 ? ` · ${d.publication.pending_changes} modification${d.publication.pending_changes > 1 ? 's' : ''} non publiée${d.publication.pending_changes > 1 ? 's' : ''}` : ' · à jour'}`
      : 'Brouillon — jamais publié'
    : null
  return (
    <main className="lc-page lc-page--management lc-stagger hub-page">
      <Trail />
      <SmartBack fallback="/equipe" />
      <header className="hub-hero">
        <h1 className="lc-title">Temps</h1>
        <p className="hub-hero__sub">Le temps de travail de l’équipe : planning aujourd’hui, pointage et compteurs demain.</p>
      </header>
      <div className="hub-grid hub-grid--temps">
        <HubTile route="/equipe/temps/planning" icon={CalendarDays} size="lg"
          title="Planning" headline={headline}
          description="La semaine d’équipe — brouillon, publication versionnée, notifications ciblées."
          meta={meta} />
        <HubTile route="/equipe/temps/validation" icon={ClipboardList} size="lg"
          title="Validation"
          headline={hub ? (hub.to_approve > 0 ? `${hub.to_approve} journée${hub.to_approve > 1 ? 's' : ''} à traiter` : 'À jour') : '…'}
          description="Prévu / pointé / validé, écart par écart."
          meta={hub && hub.anomalies > 0 ? `${hub.anomalies} anomalie${hub.anomalies > 1 ? 's' : ''} de pointage` : null} />
        <HubTile route="/equipe/temps/pointeuse" icon={CalendarDays}
          title="Pointeuse"
          headline={hub ? (hub.stations_total > 0 ? `${hub.stations_online}/${hub.stations_total} station${hub.stations_total > 1 ? 's' : ''} en ligne` : 'Aucune station') : '…'}
          description="Stations kiosk, codes personnels, politique de pointage." />
        <HubTile route="/equipe/temps/conges" icon={CalendarDays}
          title="Congés & absences"
          headline={hub ? (hub.leave_requests > 0 ? `${hub.leave_requests} demande${hub.leave_requests > 1 ? 's' : ''} à traiter` : 'À jour') : '…'}
          description="Demandes des salariés, approbations, impacts planning." />
        <HubTile route="/equipe/temps/disponibilites" icon={ClipboardList}
          title="Disponibilités"
          headline={hub ? (hub.campaigns_open > 0 ? `${hub.campaigns_open} campagne${hub.campaigns_open > 1 ? 's' : ''} ouverte${hub.campaigns_open > 1 ? 's' : ''}` : 'Aucune campagne') : '…'}
          description="Ouvertures exceptionnelles — les réponses remplacent WhatsApp." />
        <HubTile route="/equipe/temps/compteurs" icon={ClipboardList}
          title="Compteurs"
          description="CP, récupération, repos compensateur — solde = somme des mouvements." />
      </div>
    </main>
  )
}
